import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Search, FileText, 
  Calendar, Tag, Image as ImageIcon, Save, X, Globe, ArrowLeft, Upload, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API = '/api';

// Predefined categories
const PREDEFINED_CATEGORIES = [
  'Recruitment',
  'HR Consulting', 
  'Training',
  'Industry Insights',
  'Hiring in Germany',
  'Corporate Training',
  'Executive Search',
  'Career Development'
];

// Quill editor modules
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link', 'image'],
    [{ 'align': [] }],
    [{ 'direction': 'rtl' }],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'indent', 'link', 'image', 'align', 'direction'
];

// Helper to generate slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export default function AdminInsightsPage() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Editor dialog state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingInsight, setEditingInsight] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [insightToDelete, setInsightToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    slug: '',
    title_en: '',
    title_ar: '',
    excerpt_en: '',
    excerpt_ar: '',
    content_en: '',
    content_ar: '',
    seo_title_en: '',
    seo_title_ar: '',
    meta_description_en: '',
    meta_description_ar: '',
    featured_image: '',
    category: '',
    tags: [],
    status: 'draft',
    publish_date: ''
  });
  const [newTag, setNewTag] = useState('');
  const [customCategory, setCustomCategory] = useState('');

  useEffect(() => {
    fetchInsights();
  }, [statusFilter]);

  // Upload featured image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, GIF, and WEBP images are allowed');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/upload/insight-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, featured_image: data.url }));
        toast.success('Image uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const response = await fetch(`${API}/admin/insights?limit=100${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      title_en: '',
      title_ar: '',
      excerpt_en: '',
      excerpt_ar: '',
      content_en: '',
      content_ar: '',
      seo_title_en: '',
      seo_title_ar: '',
      meta_description_en: '',
      meta_description_ar: '',
      featured_image: '',
      category: '',
      tags: [],
      status: 'draft',
      publish_date: ''
    });
    setNewTag('');
    setCustomCategory('');
  };

  const openNewInsight = () => {
    resetForm();
    setEditingInsight(null);
    setEditorOpen(true);
  };

  const openEditInsight = async (insight) => {
    setFormData({
      slug: insight.slug || '',
      title_en: insight.title_en || '',
      title_ar: insight.title_ar || '',
      excerpt_en: insight.excerpt_en || '',
      excerpt_ar: insight.excerpt_ar || '',
      content_en: insight.content_en || '',
      content_ar: insight.content_ar || '',
      seo_title_en: insight.seo_title_en || '',
      seo_title_ar: insight.seo_title_ar || '',
      meta_description_en: insight.meta_description_en || '',
      meta_description_ar: insight.meta_description_ar || '',
      featured_image: insight.featured_image || '',
      category: insight.category || '',
      tags: insight.tags || [],
      status: insight.status || 'draft',
      publish_date: insight.publish_date ? insight.publish_date.split('T')[0] : ''
    });
    setEditingInsight(insight);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.title_en || !formData.title_ar) {
      toast.error('Please provide both English and Arabic titles');
      return;
    }
    if (!formData.excerpt_en || !formData.excerpt_ar) {
      toast.error('Please provide both English and Arabic excerpts');
      return;
    }
    if (!formData.content_en || !formData.content_ar) {
      toast.error('Please provide both English and Arabic content');
      return;
    }
    if (!formData.slug) {
      toast.error('Please provide a URL slug');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingInsight 
        ? `${API}/admin/insights/${editingInsight.insight_id}`
        : `${API}/admin/insights`;
      const method = editingInsight ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save insight');
      }

      toast.success(editingInsight ? 'Insight updated successfully' : 'Insight created successfully');
      setEditorOpen(false);
      fetchInsights();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!insightToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/insights/${insightToDelete.insight_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to delete insight');
      }

      toast.success('Insight deleted successfully');
      fetchInsights();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleteDialogOpen(false);
      setInsightToDelete(null);
    }
  };

  const handleAddTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({ ...formData, tags: [...formData.tags, newTag] });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({ 
      ...formData, 
      tags: formData.tags.filter(tag => tag !== tagToRemove) 
    });
  };

  const handleTitleChange = (value, lang) => {
    setFormData({ ...formData, [`title_${lang}`]: value });
    // Auto-generate slug from English title if slug is empty
    if (lang === 'en' && !formData.slug) {
      setFormData(prev => ({ ...prev, slug: generateSlug(value) }));
    }
  };

  // Filter insights based on search
  const filteredInsights = insights.filter(insight => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      insight.title_en?.toLowerCase().includes(search) ||
      insight.title_ar?.toLowerCase().includes(search) ||
      insight.category?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#111827]">WeDo Insights</h1>
          <p className="text-gray-600 mt-1">Manage bilingual articles and content</p>
        </div>
        <Button 
          onClick={openNewInsight}
          className="bg-[#33AADD] hover:bg-[#2890BD]"
          data-testid="new-insight-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Article
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Insights List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg p-4 animate-pulse">
              <div className="flex gap-4">
                <div className="w-24 h-16 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredInsights.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No Articles Yet</h3>
            <p className="text-gray-500 mb-4">Create your first insight article to get started</p>
            <Button onClick={openNewInsight}>
              <Plus className="h-4 w-4 mr-2" />
              Create Article
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredInsights.map(insight => (
            <Card key={insight.insight_id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Thumbnail */}
                  <div className="w-full sm:w-32 h-24 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    {insight.featured_image ? (
                      <img 
                        src={insight.featured_image} 
                        alt={insight.title_en}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold text-[#111827] truncate">
                          {insight.title_en}
                        </h3>
                        <p className="text-sm text-gray-500 truncate" dir="rtl">
                          {insight.title_ar}
                        </p>
                      </div>
                      <Badge 
                        className={insight.status === 'published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                        }
                      >
                        {insight.status === 'published' ? (
                          <><Eye className="h-3 w-3 mr-1" /> Published</>
                        ) : (
                          <><EyeOff className="h-3 w-3 mr-1" /> Draft</>
                        )}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                      {insight.category && (
                        <span className="flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          {insight.category}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(insight.publish_date || insight.created_at)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openEditInsight(insight)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      {insight.status === 'published' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(`/insights/${insight.slug}`, '_blank')}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setInsightToDelete(insight);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#33AADD]" />
              {editingInsight ? 'Edit Article' : 'Create New Article'}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="content" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
              <TabsTrigger value="media">Media</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-6 mt-4">
              {/* Slug */}
              <div>
                <Label htmlFor="slug">URL Slug *</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-gray-500 text-sm">/insights/</span>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                    placeholder="article-url-slug"
                    className="flex-1"
                  />
                </div>
              </div>

              {/* English Title */}
              <div>
                <Label htmlFor="title_en">Title (English) *</Label>
                <Input
                  id="title_en"
                  value={formData.title_en}
                  onChange={(e) => handleTitleChange(e.target.value, 'en')}
                  placeholder="Article title in English"
                  className="mt-1"
                />
              </div>

              {/* Arabic Title */}
              <div>
                <Label htmlFor="title_ar">Title (Arabic) *</Label>
                <Input
                  id="title_ar"
                  value={formData.title_ar}
                  onChange={(e) => handleTitleChange(e.target.value, 'ar')}
                  placeholder="عنوان المقال بالعربية"
                  className="mt-1"
                  dir="rtl"
                />
              </div>

              {/* English Excerpt */}
              <div>
                <Label htmlFor="excerpt_en">Excerpt (English) *</Label>
                <Textarea
                  id="excerpt_en"
                  value={formData.excerpt_en}
                  onChange={(e) => setFormData({ ...formData, excerpt_en: e.target.value })}
                  placeholder="Brief summary of the article..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              {/* Arabic Excerpt */}
              <div>
                <Label htmlFor="excerpt_ar">Excerpt (Arabic) *</Label>
                <Textarea
                  id="excerpt_ar"
                  value={formData.excerpt_ar}
                  onChange={(e) => setFormData({ ...formData, excerpt_ar: e.target.value })}
                  placeholder="ملخص موجز عن المقال..."
                  className="mt-1"
                  rows={3}
                  dir="rtl"
                />
              </div>

              {/* English Content */}
              <div>
                <Label>Content (English) *</Label>
                <div className="mt-1 border rounded-md">
                  <ReactQuill
                    theme="snow"
                    value={formData.content_en}
                    onChange={(value) => setFormData({ ...formData, content_en: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write your article content here..."
                    className="bg-white"
                    style={{ minHeight: '250px' }}
                  />
                </div>
              </div>

              {/* Arabic Content */}
              <div>
                <Label>Content (Arabic) *</Label>
                <div className="mt-1 border rounded-md" dir="rtl">
                  <ReactQuill
                    theme="snow"
                    value={formData.content_ar}
                    onChange={(value) => setFormData({ ...formData, content_ar: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="اكتب محتوى المقال هنا..."
                    className="bg-white"
                    style={{ minHeight: '250px' }}
                  />
                </div>
              </div>
            </TabsContent>

            {/* SEO Tab */}
            <TabsContent value="seo" className="space-y-6 mt-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <Globe className="h-4 w-4 inline mr-1" />
                  SEO fields help your content rank better in search engines. If left empty, the title and excerpt will be used.
                </p>
              </div>

              <div>
                <Label htmlFor="seo_title_en">SEO Title (English)</Label>
                <Input
                  id="seo_title_en"
                  value={formData.seo_title_en}
                  onChange={(e) => setFormData({ ...formData, seo_title_en: e.target.value })}
                  placeholder="SEO optimized title for Google"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Recommended: 50-60 characters</p>
              </div>

              <div>
                <Label htmlFor="seo_title_ar">SEO Title (Arabic)</Label>
                <Input
                  id="seo_title_ar"
                  value={formData.seo_title_ar}
                  onChange={(e) => setFormData({ ...formData, seo_title_ar: e.target.value })}
                  placeholder="عنوان محسّن لمحركات البحث"
                  className="mt-1"
                  dir="rtl"
                />
              </div>

              <div>
                <Label htmlFor="meta_description_en">Meta Description (English)</Label>
                <Textarea
                  id="meta_description_en"
                  value={formData.meta_description_en}
                  onChange={(e) => setFormData({ ...formData, meta_description_en: e.target.value })}
                  placeholder="Description that appears in search results..."
                  className="mt-1"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Recommended: 150-160 characters</p>
              </div>

              <div>
                <Label htmlFor="meta_description_ar">Meta Description (Arabic)</Label>
                <Textarea
                  id="meta_description_ar"
                  value={formData.meta_description_ar}
                  onChange={(e) => setFormData({ ...formData, meta_description_ar: e.target.value })}
                  placeholder="وصف يظهر في نتائج البحث..."
                  className="mt-1"
                  rows={3}
                  dir="rtl"
                />
              </div>
            </TabsContent>

            {/* Media Tab */}
            <TabsContent value="media" className="space-y-6 mt-4">
              <div>
                <Label>Featured Image</Label>
                
                {/* Upload Button */}
                <div className="space-y-4 mt-2">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="insight-image-upload"
                  />
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="flex items-center gap-2"
                    >
                      {uploadingImage ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Upload Image
                        </>
                      )}
                    </Button>
                    <span className="text-xs text-gray-500">JPG, PNG, GIF, WEBP (max 5MB)</span>
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">Or enter URL manually</span>
                    </div>
                  </div>

                  {/* Manual URL Input */}
                  <Input
                    id="featured_image"
                    value={formData.featured_image}
                    onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500">
                    Recommended size: 1200x630 pixels for optimal social sharing
                  </p>
                </div>
              </div>

              {/* Image Preview */}
              {formData.featured_image && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Preview</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, featured_image: '' })}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                  <img 
                    src={formData.featured_image} 
                    alt="Featured preview"
                    className="max-w-full max-h-64 rounded-lg shadow-sm object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      toast.error('Failed to load image preview');
                    }}
                  />
                </div>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6 mt-4">
              {/* Category */}
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => {
                    if (value === '_custom_') {
                      // Don't set category yet, user will type custom
                    } else {
                      setFormData({ ...formData, category: value });
                      setCustomCategory('');
                    }
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREDEFINED_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="_custom_">+ Add Custom Category</SelectItem>
                  </SelectContent>
                </Select>
                {formData.category === '_custom_' && (
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      placeholder="Enter custom category"
                    />
                    <Button 
                      type="button"
                      onClick={() => {
                        if (customCategory) {
                          setFormData({ ...formData, category: customCategory });
                          setCustomCategory('');
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddTag}>
                    Add
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="gap-1">
                        {tag}
                        <X 
                          className="h-3 w-3 cursor-pointer" 
                          onClick={() => handleRemoveTag(tag)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Status */}
              <div>
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">
                      <span className="flex items-center gap-2">
                        <EyeOff className="h-4 w-4" /> Draft
                      </span>
                    </SelectItem>
                    <SelectItem value="published">
                      <span className="flex items-center gap-2">
                        <Eye className="h-4 w-4" /> Published
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Publish Date */}
              <div>
                <Label htmlFor="publish_date">Publish Date</Label>
                <Input
                  id="publish_date"
                  type="date"
                  value={formData.publish_date}
                  onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use current date when publishing
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#33AADD] hover:bg-[#2890BD]"
            >
              {saving ? (
                'Saving...'
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingInsight ? 'Update Article' : 'Create Article'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Article?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{insightToDelete?.title_en}"? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
